import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Box, Container, Stack, Typography } from '@mui/material';

import {
  trackClickStructuredItemDiscard,
  trackClickStructuredItemGenerate,
  trackClickStructuredItemGenerateCancel,
  trackClickStructuredItemGenerateConfirm,
} from '../utils/analytics/analytics';
import { trackSentryError } from '../utils/analytics/sentry';
import {
  handleApiError as handleApiErrorUtil,
  revealStructuredItems,
} from '../utils/api';
import { colors } from '../utils/colors';
import { DEFAULT_GENERATION_COST } from '../utils/const';
import { deleteStructuredItemDb } from '../utils/db';
import { useFirebaseLoginState } from '../utils/hooks';
import { RootState } from '../utils/store';
import { LoadingStatus, SectionName, StructuredItem, TabName } from '../utils/types';
import { GradientButton } from './minicomponents/GradientButton';
import { GradientCircularProgress } from './minicomponents/GradientCircularProgress';
import { IconClose, IconError, IconRocketLaunchFilled } from './minicomponents/icons';
import { ButtonWithIcon } from './minicomponents/minicomponents';
import AlertPopup from './popups/AlertPopup';
import ConfirmationPopup from './popups/ConfirmationPopup';
import UpgradePopup from './popups/UpgradePopup';

interface IdeaCardProps {
  idea: StructuredItem;
  parentItemId: string;
  isGenerating?: boolean;
  sectionName?: SectionName;
  tabName?: TabName;
}

const generationCost = DEFAULT_GENERATION_COST;

export const IdeaCard: React.FC<IdeaCardProps> = ({
  idea,
  parentItemId,
  isGenerating = false,
  sectionName,
  tabName,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isUpgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const { userId } = useFirebaseLoginState();
  const state = useSelector((state: RootState) => state.appState);
  const selectedCampaignId = useSelector(
    (state: RootState) => state.appState.selectedCampaign
  )?.id;

  const handleError = useCallback((error: unknown) => {
    trackSentryError('Error in IdeaCard', { error });
    handleApiErrorUtil({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setError,
      onSetErrorAlertOpen: (isOpen) => {
        setIsAlertOpen(isOpen);
        if (!isOpen) setError(null);
      },
    });
  }, []);

  const handleGenerateClick = () => {
    if (sectionName && tabName) {
      trackClickStructuredItemGenerate(sectionName, tabName);
    }
    setIsConfirmOpen(true);
  };

  const confirmGenerate = async () => {
    if (!userId || !selectedCampaignId) return;
    if (sectionName && tabName) {
      trackClickStructuredItemGenerateConfirm(sectionName, tabName);
    }
    setIsConfirmOpen(false);
    setIsLoading(true);
    try {
      await revealStructuredItems(
        userId,
        selectedCampaignId,
        parentItemId,
        [idea.id!],
        state.session
      );
    } catch (error) {
      trackSentryError('Error revealing item content', { error });
      console.error('Error revealing item content', error);
      handleError(error);
      setIsLoading(false); // keep spinner only on success until listener swaps UI
    }
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (sectionName && tabName) {
      trackClickStructuredItemDiscard(sectionName, tabName);
    }
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userId || !selectedCampaignId) return;

    try {
      await deleteStructuredItemDb(selectedCampaignId, parentItemId, idea.id!);
      // Firestore listener in SMMStructuredView will handle the UI update automatically.
    } catch (error) {
      trackSentryError('Error deleting structured item', { error });
      handleError(error);
    }
  };

  return (
    <Container
      sx={{
        position: 'relative',
        padding: '15px 39px 23px',
        border: '1px dashed #D1D1D1',
        borderRadius: '6px',
      }}
    >
      {idea.status === LoadingStatus.LOADING ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: '8px' }}>
          <GradientCircularProgress size={16} />
          <Typography sx={{ color: colors.text.hint, fontWeight: 'medium' }}>
            {t('app.structuredItems.actions.generateMore.progressMessage')}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="end"
            sx={{ height: '40px' }}
          >
            <Typography
              variant="h6"
              sx={{ mr: 12, fontSize: '90%', color: 'text.secondary' }}
            >
              {t('app.structuredItems.types.' + idea.type + '_idea')}
            </Typography>

            <ButtonWithIcon
              icon={<IconClose />}
              onClick={handleDeleteClick}
              sx={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                zIndex: 1,
              }}
            />
          </Stack>
          <Typography variant="body1">
            <strong>{t('app.structuredItems.topic')}</strong>
            &nbsp;
            {idea.topic}
          </Typography>
          <Typography variant="body1">
            <strong>{t('app.structuredItems.focus')}</strong>
            &nbsp;
            {idea.focus}
          </Typography>
          <Box
            sx={{
              pt: '8px',
              minHeight: '48px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isLoading || isGenerating ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 2 }}>
                <GradientCircularProgress size={16} />
                <Typography
                  sx={{
                    color: colors.text.hint,
                    fontWeight: 'medium',
                    lineHeight: '24.5px',
                  }}
                >
                  {t('app.structuredItems.actions.generatePost.progressMessage')}
                </Typography>
              </Stack>
            ) : idea.status === LoadingStatus.FAILED ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconError sx={{ color: colors.text.error }} />
                <Typography>
                  <span style={{ color: colors.text.error }}>
                    {t('app.errors.failedToGenerate')}
                  </span>{' '}
                  <span style={{ color: colors.text.hint }}>
                    {t('app.errors.noChargeForFailedGeneration')}
                  </span>
                </Typography>
                <GradientButton
                  buttonText={t('app.buttons.retry')}
                  onSubmitButtonHandler={handleGenerateClick}
                  version="text"
                  iconLeft={<IconRocketLaunchFilled />}
                />
              </Stack>
            ) : (
              <GradientButton
                onSubmitButtonHandler={handleGenerateClick}
                buttonText={t('app.structuredItems.actions.generatePost.buttonText')}
                version="text"
                iconLeft={<IconRocketLaunchFilled />}
                sx={{ minWidth: 'auto' }}
              />
            )}
          </Box>
        </Stack>
      )}
      <AlertPopup
        open={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubmit={() => setIsAlertOpen(false)}
        alertText={error || ''}
        title={t('app.popups.error.title')}
        buttonText={t('app.buttons.ok')}
      />
      <ConfirmationPopup
        open={isConfirmOpen}
        onClose={() => {
          if (sectionName && tabName) {
            trackClickStructuredItemGenerateCancel(sectionName, tabName);
          }
          setIsConfirmOpen(false);
        }}
        onSubmit={confirmGenerate}
        title={t('app.structuredItems.actions.generatePost.confirmationTitle', {
          postType: t('app.structuredItems.types.' + idea.type),
        })}
        message={t('app.structuredItems.actions.generatePost.confirmationMessage', {
          topic: idea.topic,
        })}
        confirmButtonText={t('app.popups.generate.buttonText', {
          count: generationCost,
        })}
        showRocketButton={true}
      />
      <ConfirmationPopup
        open={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onSubmit={handleConfirmDelete}
        title={t('app.structuredItems.actions.deleteIdea.confirmationTitle')}
        message={t('app.structuredItems.actions.deleteIdea.confirmationMessage', {
          topic: idea.topic,
        })}
        confirmButtonText={t('app.buttons.delete')}
        confirmButtonColor="error.main"
        disclaimer={t('app.popups.disclaimers.cannotBeUndone')}
      />
      <UpgradePopup
        open={isUpgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleError}
      />
    </Container>
  );
};
